import React from 'react';

/**
 * ScensobConnectLogo
 * ------------------
 * A custom-designed brand mark for ScensobConnect — a staffing marketplace
 * that connects three parties: workers, employers, and agencies.
 *
 * The mark is built around a stylized "SC" monogram woven into three
 * connection nodes (the people on the platform) inside a rounded square. The
 * gradient mirrors the brand palette already used across dashboards
 * (violet → purple → indigo), keeping visual continuity with the rest of the
 * app while replacing the generic Shield icon.
 *
 * Variants:
 *   variant="mark"       → icon only (square)
 *   variant="wordmark"   → wordmark only (text)
 *   variant="full"       → icon + wordmark side by side (default)
 *
 * Props:
 *   size       → height in pixels for the mark (default 36)
 *   className  → applied to the wrapper for spacing tweaks
 *   subtitle   → optional subtitle rendered under the wordmark
 *   wordmarkColor → override text color (defaults to currentColor)
 */
export function ScensobConnectLogo({
  variant = 'full',
  size = 36,
  className = '',
  subtitle = '',
  wordmarkColor = '',
}) {
  const id = `scn-${React.useId().replace(/:/g, '')}`;
  const stroke = Math.max(2, Math.round(size * 0.06));

  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="ScensobConnect"
      data-testid="scensob-logo-mark"
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="55%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>
        <linearGradient id={`${id}-arc`} x1="14" y1="14" x2="50" y2="50" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#E0E7FF" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      {/* Rounded square background */}
      <rect x="0" y="0" width="64" height="64" rx="16" fill={`url(#${id}-bg)`} />

      {/* Subtle inner glow */}
      <rect x="2" y="2" width="60" height="60" rx="14" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />

      {/* Connection arc — a stylized "S" curve that links the three nodes */}
      <path
        d="M 18 22 C 18 14, 30 14, 32 22 C 34 30, 18 30, 18 38 C 18 46, 36 50, 46 42"
        stroke={`url(#${id}-arc)`}
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="none"
      />

      {/* Three nodes representing Worker / Employer / Agency */}
      <circle cx="18" cy="22" r="3.5" fill="#FFFFFF" />
      <circle cx="32" cy="32" r="3.5" fill="#FFFFFF" />
      <circle cx="46" cy="42" r="3.5" fill="#FFFFFF" />

      {/* Tiny accent dot on top-right to add brand spark */}
      <circle cx="50" cy="18" r="2" fill="#A78BFA" />
    </svg>
  );

  const wordmark = (
    <span
      className="leading-none select-none"
      style={{ fontFamily: "Inter, -apple-system, system-ui, sans-serif", color: wordmarkColor || undefined }}
      data-testid="scensob-logo-wordmark"
    >
      <span
        className="font-extrabold tracking-tight"
        style={{ fontSize: Math.round(size * 0.6) }}
      >
        scensob
      </span>
      <span
        className="font-light tracking-tight"
        style={{ fontSize: Math.round(size * 0.6), letterSpacing: '-0.01em' }}
      >
        connect
      </span>
    </span>
  );

  if (variant === 'mark') return <span className={className}>{mark}</span>;
  if (variant === 'wordmark') return (
    <span className={`inline-flex flex-col ${className}`}>
      {wordmark}
      {subtitle && (
        <span className="text-xs uppercase tracking-widest opacity-60 mt-0.5">
          {subtitle}
        </span>
      )}
    </span>
  );

  // Default: full lockup
  return (
    <span className={`inline-flex items-center gap-3 ${className}`}>
      {mark}
      <span className="inline-flex flex-col leading-tight">
        {wordmark}
        {subtitle && (
          <span className="text-xs uppercase tracking-widest opacity-60 mt-0.5">
            {subtitle}
          </span>
        )}
      </span>
    </span>
  );
}

export default ScensobConnectLogo;
